/**
 * Sharing + referrals shared constants (batch 6 of the July 2026 design
 * handoff). Single source of truth for BOTH the web client and the API server:
 *   - share/referral analytics validators (consumed by activity-events.ts),
 *   - the bounded lengths the server trusts when validating those events, and
 *   - the referral-economy constants (annual credit cap, per-credit months).
 *
 * Kept provider-agnostic and dependency-free so the API (which cannot import
 * from apps/web) and the web client share exactly one definition.
 */

// ─── Share kinds + formats ───────────────────────────────

/** What can be shared. Only a reading today; the list is the allowlist the
 *  server validates `share_opened.type` / `share_completed.type` against. */
export const SHARE_TYPES = ["reading"] as const;
export type ShareType = (typeof SHARE_TYPES)[number];

const SHARE_TYPE_SET = new Set<string>(SHARE_TYPES);

/** Type guard: is `value` a registered share type? */
export function isShareType(value: unknown): value is ShareType {
  return typeof value === "string" && SHARE_TYPE_SET.has(value);
}

/** The two rendered share formats — a vertical story card or a square post. */
export const SHARE_FORMATS = ["story", "post"] as const;
export type ShareFormat = (typeof SHARE_FORMATS)[number];

const SHARE_FORMAT_SET = new Set<string>(SHARE_FORMATS);

/** Type guard: is `value` a registered share format? */
export function isShareFormat(value: unknown): value is ShareFormat {
  return typeof value === "string" && SHARE_FORMAT_SET.has(value);
}

// ─── Bounded lengths (server-trusted caps) ───────────────

/** Max length of a share shortid (the `shares.id` primary key, carried in the
 *  public `/r/:shortid` link and the `share_link_visited` event). */
export const SHARE_SHORTID_MAX_LEN = 24;

/** Max length of a referral code (the derived handle in `referral_codes.code`,
 *  carried in `referral_signup` / `referral_activated` events). */
export const REFERRAL_CODE_MAX_LEN = 32;

/** Max length of the `deck` prop on `practitioner_attribution` (a deck id — see
 *  DECK_REGISTRY — kept a bounded string so a new deck needs no code change). */
export const ATTRIBUTION_DECK_MAX_LEN = 48;

// ─── Referral economy ────────────────────────────────────

/**
 * Cap on how many referral credit-months a single referrer can earn per
 * rolling year. Enforced server-side (Task 3) when granting a credit; prevents
 * a referral farm from minting unbounded free Cosmic months.
 */
export const REFERRAL_CAP_PER_YEAR = 12;

/** Months of free Cosmic granted per qualifying referral credit (matches the
 *  `referral_credits.months` column default). */
export const REFERRAL_CREDIT_MONTHS = 1;
